import React, { useState } from 'react';
import { 
  Box, 
  Paper,
  Tabs,
  Tab,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  MapContainer, 
  TileLayer, 
  ZoomControl, 
  ScaleControl,
  Marker,
  Popup,
  useMapEvents
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';
import {
  Business as NetworkIcon,
  Psychology as ScenarioIcon,
  TrendingUp as MarketIcon,
  Groups as PopulationIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Close as CloseIcon,
  ThreeDRotation as ThreeDIcon
} from '@mui/icons-material';
import FacilityDashboard from './FacilityDashboard';
import MapFilters from './MapFilters';
import MarketIntelligence from './MarketIntelligence';
import FilterToggleButton from '../../components/map/FilterToggleButton';
import { filterPanelStyles } from '../../styles/map/filterPanelStyles';
import ScenarioBuilder from './ScenarioBuilder';
import PopulationCohorts from './PopulationCohorts';

// Fix for default marker icons in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: iconRetina,
  iconUrl: icon,
  shadowUrl: iconShadow
});

// Example medical facility data - replace with Supabase data later
const sampleFacilities = [
  {
    id: 1,
    name: "Orlando Regional Medical Center",
    position: [28.5223, -81.3774],
    type: "Hospital",
    beds: 808,
    level: "Level 1 Trauma",
    address: "52 W Underwood St",
    city: "Orlando",
    state: "FL",
    zip: "32806"
  },
  {
    id: 2,
    name: "AdventHealth Orlando",
    position: [28.5758, -81.3656],
    type: "Hospital",
    beds: 1368,
    level: "Tertiary Care",
    address: "601 E Rollins St",
    city: "Orlando",
    state: "FL",
    zip: "32803"
  },
  {
    id: 3,
    name: "Winter Park Memorial Hospital",
    position: [28.5947, -81.3543],
    type: "Hospital",
    beds: 320,
    level: "Community Hospital",
    address: "200 N Lakemont Ave",
    city: "Winter Park",
    state: "FL",
    zip: "32792"
  }
];

const Map: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [selectedFacility, setSelectedFacility] = useState<typeof sampleFacilities[0] | null>(null);
  const [showFilters, setShowFilters] = useState(true);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleApplyFilters = (filters: string[]) => {
    setActiveFilters(filters);
    // TODO: Apply filters to map data
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const MapClickHandler = () => {
    useMapEvents({
      click: () => {
        setSelectedFacility(null);
      },
    });
    return null;
  };

  const renderMap = () => (
    <Box sx={{ 
      height: '100%',
      width: '100%',
      position: 'relative',
      '& .leaflet-container': {
        height: '100%',
        width: '100%',
        borderRadius: 1,
        zIndex: 0
      }
    }}>
      <MapContainer 
        center={[28.5383, -81.3792]} 
        zoom={10}
        scrollWheelZoom={true}
        zoomControl={false}
        style={{ height: '100%', width: '100%' }}
      >
        <MapClickHandler />
        <ZoomControl position="topright" />
        <ScaleControl position="bottomright" />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {sampleFacilities.map(facility => (
          <Marker 
            key={facility.id}
            position={facility.position as L.LatLngExpression}
            eventHandlers={{
              click: () => {
                setSelectedFacility(facility);
              },
            }}
          />
        ))}
      </MapContainer>
      
      {selectedFacility && (
        <FacilityDashboard
          facility={selectedFacility}
          onClose={() => setSelectedFacility(null)}
        />
      )}
    </Box>
  );

  const renderCareNetworkView = () => (
    <Box sx={{ height: 'calc(100vh - 240px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>My Care Network</Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage your healthcare network facilities and providers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FilterToggleButton showFilters={showFilters} onToggle={toggleFilters} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
          >
            Add Facility
          </Button>
        </Box>
      </Box>
      <Box sx={{ 
        flexGrow: 1,
        minHeight: 0,
        display: 'flex',
        gap: 0
      }}>
        <Paper sx={filterPanelStyles.panel({ showFilters })}>
          {showFilters && (
            <Box sx={filterPanelStyles.container}>
              <Box sx={filterPanelStyles.content}>
                <MapFilters onApplyFilters={handleApplyFilters} />
              </Box>
            </Box>
          )}
        </Paper>
        <Paper 
          elevation={3} 
          sx={{ 
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: showFilters ? '0 4px 4px 0' : '4px'
          }}
        >
          {renderMap()}
        </Paper>
      </Box>
    </Box>
  );

  const renderScenarioBuilderView = () => (
    <ScenarioBuilder />
  );

  const renderMarketIntelligenceView = () => (
    <MarketIntelligence renderMap={renderMap} />
  );

  const renderPopulationCohortsView = () => (
    <PopulationCohorts />
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="map view tabs"
          sx={{ flexGrow: 1 }}
        >
        <Tab 
          icon={<NetworkIcon />} 
          label="Care Network" 
          iconPosition="start"
        />
        <Tab 
          icon={<ScenarioIcon />} 
          label="Scenario Builder" 
          iconPosition="start"
        />
        <Tab 
          icon={<MarketIcon />} 
          label="Market Intelligence" 
          iconPosition="start"
        />
        <Tab 
          icon={<PopulationIcon />} 
          label="Population Cohorts" 
          iconPosition="start"
        />
      </Tabs>
      <Button
        variant="outlined"
        startIcon={<ThreeDIcon />}
        onClick={() => navigate('/3dmap')}
        sx={{
          mr: 2,
          borderColor: 'rgba(138,43,226,0.3)',
          color: '#FFFFFF',
          '&:hover': {
            borderColor: 'rgba(0,180,255,0.5)',
            backgroundColor: 'rgba(0,180,255,0.1)'
          }
        }}
      >
        3D View
      </Button>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        {tabValue === 0 && renderCareNetworkView()}
        {tabValue === 1 && renderScenarioBuilderView()}
        {tabValue === 2 && renderMarketIntelligenceView()}
        {tabValue === 3 && renderPopulationCohortsView()}
      </Box>
    </Box>
  );
};

export default Map; 